/**
 * host-squeeze — the docked-sidebar page squeeze (FLO-519, part of FLO-514).
 *
 * When the website agent renders as a right-hand rail (displayMode 'sidebar'),
 * the host page has to give up that strip of viewport instead of hiding
 * underneath it. This module is the ONLY thing that writes host-page styles for
 * that: it injects one `<style id="floe-host-squeeze" media="screen">` into
 * `document.head` and flips a single attribute on `<html>`.
 *
 * Why a stylesheet + attribute rather than inline styles (the mechanism
 * Qualified.com ships, verified live 2026-08-11):
 *
 *   html { --floe-docked-width: 0px; margin-right: var(--floe-docked-width);
 *          transition: --floe-docked-width .3s ease-out }
 *   html[data-floe-docked] { --floe-docked-width: 400px }
 *
 * `--floe-docked-width` is registered via `CSS.registerProperty` as a `<length>`,
 * which makes it ANIMATABLE — so the margin, the max-width and any host element
 * that reads the published `--floe-docked-viewport-offset` all move in lockstep
 * on one 0.3s ease-out curve. Browsers without `registerProperty` (Safari < 16.4)
 * fall back to transitioning `margin-right` directly: the page still animates,
 * just without the var-derived elements riding the same curve.
 *
 * The geometry lives in the BASE rule, driven by the var, and the docked rule
 * only changes the var — that asymmetry is what makes the CLOSE animate. If
 * `margin-right` were declared inside `html[data-floe-docked]`, removing the
 * attribute would delete the declaration outright and the page would jump back
 * to full width in one frame while the still-animating offset var dragged every
 * cooperating fixed element behind it for 300ms.
 *
 * `--floe-docked-viewport-offset` is published (= `--floe-docked-width`) as the
 * cooperation hook for host `position: fixed` elements — a fixed navbar's
 * containing block is the viewport, not `<html>`, so it does NOT squeeze with
 * the page; it opts in with `right: var(--floe-docked-viewport-offset, 0px)`.
 * (The zero-cooperation fix — `container-type: inline-size` on `<html>` plus a
 * body scroller — is deliberately deferred; see the FLO-514 plan, slice 3.)
 *
 * State lives ENTIRELY in the attribute and the style element: nothing inline is
 * written to the host, so release is "remove the attribute, remove the element"
 * and there is no saved-state to restore (and nothing to leak if a teardown is
 * missed). Reference-counted like {@link ./scroll-lock}, so a duplicate apply
 * (React StrictMode's double-invoked effects in dev, two SDK instances) cannot
 * leave the page stuck squeezed.
 *
 * Known cascade side effect (accepted, not fixable in CSS): the `transition`
 * declaration on `html` replaces — it cannot append to — whatever the host page
 * declared for the root element, so a host that fades its own theme with
 * `html { transition: background-color .3s }` loses that fade while docked (and
 * for the ~360ms close tail, after which the sheet is pulled). Longhands would
 * clobber it just the same; CSS has no "add to the transition list". The
 * converse also holds: a host rule with higher specificity (`:root`, 0-1-0,
 * beats our `html`, 0-0-1) would win and drop OUR transition, in which case the
 * squeeze applies instantly instead of animating. Both are cosmetic and scoped
 * to the docked window.
 *
 * Scope: sidebar display mode only, and only in the ≥1024px squeeze tier — the
 * caller (WebsiteAgentView) owns that gate. `media="screen"` keeps print output
 * untouched; `prefers-reduced-motion: reduce` drops the transition.
 */

/** Attribute set on `<html>` while the rail is docked. Cross-package contract. */
export const DOCKED_ATTR = 'data-floe-docked';

/** id of the single injected `<style>` element. Cross-package contract. */
export const SQUEEZE_STYLE_ID = 'floe-host-squeeze';

/**
 * Host opt-in (FLO-523): put this on ONE container and the squeeze moves from
 * `<html>` to that element — Qualified's `[q-docked-target]:not(html)` pattern.
 *
 * Presence IS the opt-in: no per-site flag, no JS, nothing to configure. It is
 * the escape hatch for a page whose root cannot take a margin (a `100vw` grid, a
 * fixed app shell, a host that already animates `html`), and for one whose real
 * scroll container is an inner div. The published
 * {@link DOCKED_OFFSET_VAR} keeps working alongside it.
 *
 * It is an ALTERNATIVE to the containment harness (`sidebarCompat.containment`,
 * see ./squeeze-compat), not a companion: containment squeezes fixed elements by
 * making `<html>`'s box their containing block, and this attribute is precisely
 * the case where `<html>`'s box is the thing that does NOT move.
 */
export const DOCK_TARGET_ATTR = 'data-floe-dock-target';

/** The animatable length custom property that drives the whole squeeze. */
export const DOCKED_WIDTH_VAR = '--floe-docked-width';

/**
 * Published mirror of {@link DOCKED_WIDTH_VAR}. Host pages read this to offset
 * their own `position: fixed` chrome (which the squeeze cannot reach).
 */
export const DOCKED_OFFSET_VAR = '--floe-docked-viewport-offset';

/** Squeeze/slide duration. Shared with the rail's slide-in so they stay in sync. */
export const SQUEEZE_DURATION_MS = 300;

/** The one motion curve for every open/close move in sidebar mode. */
export const SQUEEZE_TRANSITION = '0.3s ease-out';

/**
 * Grace added to {@link SQUEEZE_DURATION_MS} before the stylesheet is pulled on
 * release — the close has to finish animating first (see releaseSqueeze).
 */
const STYLE_REMOVAL_GRACE_MS = 60;

// ---------------------------------------------------------------------------
// Pure core — no DOM. Everything below the fold is a thin driver over these.
// ---------------------------------------------------------------------------

/**
 * Coerce a caller-supplied width to a sane CSS pixel length. Junk (NaN, a
 * string, a negative) collapses to 0, which renders as "not squeezed" rather
 * than as `margin-right: NaNpx` (an invalid declaration that would drop the
 * whole rule and silently disable the squeeze).
 */
export function normalizeSqueezeWidth(width: unknown): number {
  const n = typeof width === 'number' && Number.isFinite(width) ? Math.round(width) : 0;
  return n > 0 ? n : 0;
}

/**
 * The squeeze stylesheet.
 *
 * @param widthPx      docked width in CSS px.
 * @param animatableVar true when `CSS.registerProperty` accepted
 *        {@link DOCKED_WIDTH_VAR} — the var itself is then transitionable and
 *        everything derived from it animates together. False → transition
 *        `margin-right` directly so the page still moves on 0.3s ease-out.
 */
export function buildSqueezeCss(widthPx: unknown, animatableVar: boolean): string {
  const width = normalizeSqueezeWidth(widthPx);
  const transitioned = animatableVar ? DOCKED_WIDTH_VAR : 'margin-right';
  // `max-width` can only be DERIVED from the var, never transitioned itself
  // (`none` → a length is not interpolable), so it belongs on the registered
  // path only. On the fallback path an unregistered var flips in one frame, so
  // the max-width would clamp the page instantly while `margin-right` eased for
  // 300ms — a snap with a decorative transition beside it. `overflow-x` below
  // does the same 100vw-clipping job on both paths, so dropping it costs
  // nothing but the belt.
  const maxWidth = animatableVar ? `max-width:calc(100vw - var(${DOCKED_WIDTH_VAR}));` : '';
  // Same registered-path-only rule for the opt-in container's width longhands
  // (see the rule below for why all three, and why not on the fallback path).
  const squeezed = `calc(100% - var(${DOCKED_WIDTH_VAR}))`;
  const dockTargetWidths = animatableVar
    ? `width:${squeezed};min-width:${squeezed};max-width:${squeezed};`
    : '';
  return [
    // Defaults, GEOMETRY and the transition, all declared unconditionally (not
    // only while docked) so BOTH directions animate. The open transition needs a
    // prior computed value of 0px to move from; the close needs the geometry to
    // outlive the attribute — margin-right is not itself in the transition list
    // on the registered path, so a declaration that disappears with the
    // attribute would snap rather than ease (see releaseSqueeze).
    `html{${DOCKED_WIDTH_VAR}:0px;${DOCKED_OFFSET_VAR}:var(${DOCKED_WIDTH_VAR});` +
      `margin-right:var(${DOCKED_WIDTH_VAR});${maxWidth}` +
      `transition:${transitioned} ${SQUEEZE_TRANSITION};}`,
    // FLO-523 — the host opt-in container (see DOCK_TARGET_ATTR). Driven by the
    // SAME var, so it rides the same curve and animates in BOTH directions.
    // The three width longhands exist because a host container may PIN any of
    // them (`width:100vw`, `min-width:100%`) and a single un-squeezed longhand
    // would win over the other two — but they are derived from the var, so like
    // the `max-width` above they ship only on the registered path; unregistered,
    // they would snap in one frame beside a 300ms margin ease. `margin-right`
    // alone still squeezes a normal auto-width container there, animated.
    // `:not(html)` mirrors Qualified: the attribute on <html> would be a
    // no-op-with-extra-steps (html is already squeezed by the base rule).
    `[${DOCK_TARGET_ATTR}]:not(html){${dockTargetWidths}` +
      `margin-right:var(${DOCKED_WIDTH_VAR});transition:${transitioned} ${SQUEEZE_TRANSITION};}`,
    // …and when a target exists, <html> must NOT squeeze too (that would take
    // the width twice). `:has()` keeps this PURELY declarative — it re-evaluates
    // if the host mounts its container late, which a boot-time JS probe could
    // not. Specificity (0,1,1) beats the base `html` rule (0,0,1), so order is
    // irrelevant. On a pre-2023 engine `:has()` is an invalid selector and the
    // whole rule is dropped — the page then squeezes twice, which is why this
    // opt-in is documented as `:has()`-era only.
    `html:has([${DOCK_TARGET_ATTR}]){margin-right:0;${animatableVar ? 'max-width:none;' : ''}}`,
    // Reduced motion, for the opt-in container as well as for <html> (the same
    // override lands on `html` at the bottom of the sheet). Kept next to the
    // rule it cancels rather than merged into that block.
    `@media (prefers-reduced-motion:reduce){[${DOCK_TARGET_ATTR}]:not(html){transition:none;}}`,
    // The squeeze itself is ONE value change. overflow-x guards 100vw / w-screen
    // host content, which would otherwise overflow into the rail and add a
    // horizontal scrollbar; it is a clip, not geometry, so it may snap.
    // clip > hidden: clip only truncates paint and can never create a scroll
    // container, so host `position: sticky` machinery is untouchable by it.
    // The hidden line is the fallback for engines without `clip` (< Safari 16 /
    // Chrome 90); there it carries clip's visual effect with hidden's known
    // caveat that the viewport gets overflow-x:hidden semantics.
    `html[${DOCKED_ATTR}]{${DOCKED_WIDTH_VAR}:${width}px;overflow-x:hidden;overflow-x:clip;}`,
    `@media (prefers-reduced-motion:reduce){html{transition:none;}}`,
  ].join('\n');
}

/** Ref-count transition for an apply. `mount` = this is the outermost apply. */
export function nextStateOnApply(count: number): { count: number; mount: boolean } {
  const next = Math.max(0, count) + 1;
  return { count: next, mount: next === 1 };
}

/**
 * Ref-count transition for a release. `unmount` = the last holder let go.
 * Releasing at 0 is a no-op (an unbalanced release must not drive the count
 * negative, or the next apply would never mount).
 */
export function nextStateOnRelease(count: number): { count: number; unmount: boolean } {
  if (count <= 0) return { count: 0, unmount: false };
  const next = count - 1;
  return { count: next, unmount: next === 0 };
}

// ---------------------------------------------------------------------------
// DOM driver
// ---------------------------------------------------------------------------

let squeezeCount = 0;
let styleEl: HTMLStyleElement | null = null;
let removalTimer: ReturnType<typeof setTimeout> | null = null;
/** null = not attempted yet; memoized so we register at most once per page. */
let varIsRegistered: boolean | null = null;

function hasDom(): boolean {
  return (
    typeof document !== 'undefined' &&
    !!document.documentElement &&
    !!document.head &&
    typeof document.createElement === 'function'
  );
}

/**
 * Register {@link DOCKED_WIDTH_VAR} as a `<length>` so it can be transitioned.
 * Wrapped in try/catch: `CSS.registerProperty` is absent on Safari < 16.4, and
 * a SECOND registration of the same name throws `InvalidModificationError` —
 * which means it is already registered (a re-init, or a second SDK instance),
 * i.e. success, not a reason to drop to the fallback animation.
 */
function ensureVarRegistered(): boolean {
  if (varIsRegistered !== null) return varIsRegistered;
  varIsRegistered = false;
  try {
    const css = (globalThis as any).CSS;
    if (css && typeof css.registerProperty === 'function') {
      css.registerProperty({
        name: DOCKED_WIDTH_VAR,
        syntax: '<length>',
        inherits: true,
        initialValue: '0px',
      });
      varIsRegistered = true;
    }
  } catch (err) {
    varIsRegistered = (err as { name?: string } | null)?.name === 'InvalidModificationError';
  }
  return varIsRegistered;
}

function cancelPendingRemoval(): void {
  if (removalTimer !== null) {
    clearTimeout(removalTimer);
    removalTimer = null;
  }
}

function removeStyleEl(): void {
  removalTimer = null;
  styleEl?.remove();
  styleEl = null;
}

/** Inject (or refresh) the stylesheet and dock. */
function mountSqueeze(width: number): void {
  // A re-apply inside the close window reuses the element that is waiting to be
  // removed — no flash, no duplicate <style>.
  cancelPendingRemoval();

  const doc = document;
  if (!styleEl || !styleEl.isConnected) {
    // Adopt an orphan from an earlier instance rather than stacking a second
    // sheet (two would fight over the same rules).
    const existing = doc.getElementById(SQUEEZE_STYLE_ID) as HTMLStyleElement | null;
    styleEl = existing || doc.createElement('style');
    styleEl.id = SQUEEZE_STYLE_ID;
    // Print output must not be squeezed — the rail isn't printed either.
    styleEl.setAttribute('media', 'screen');
    if (!existing) doc.head.appendChild(styleEl);
  }
  styleEl.textContent = buildSqueezeCss(width, ensureVarRegistered());

  // Force a style/layout flush so the pre-docked computed value (0px) exists
  // before the attribute lands — without it the browser coalesces "sheet added"
  // and "attribute set" into one recalc and the page SNAPS open with no
  // transition. Reading a layout property is the standard flush.
  void doc.documentElement.offsetHeight;

  doc.documentElement.setAttribute(DOCKED_ATTR, '');
}

/**
 * Squeeze the host page by `widthPx`, reference-counted.
 *
 * Returns an idempotent release function (calling it twice releases once), same
 * contract as `lockBodyScroll()`. Safe with no DOM (SSR / the node test env):
 * returns a no-op. Re-applying with a different width while already docked
 * updates the squeeze in place — last writer wins.
 */
export function applySqueeze(widthPx: number): () => void {
  if (!hasDom()) return () => {};

  const width = normalizeSqueezeWidth(widthPx);
  squeezeCount = nextStateOnApply(squeezeCount).count;
  mountSqueeze(width);

  let released = false;
  return () => {
    if (released) return;
    released = true;
    releaseSqueeze();
  };
}

/**
 * Drop one squeeze reference; on the last one, undock the page.
 *
 * The attribute is removed immediately — that alone animates the page back,
 * because the geometry lives in the base rule and follows the var down to 0px.
 * The stylesheet is pulled only after the transition has run: removing it in the
 * same tick would delete the `transition` declaration AND the margin in one go
 * and snap the page to full width. A re-apply during that window cancels the
 * removal, so open/close cycles leave no residue and no orphan `<style>`.
 */
export function releaseSqueeze(): void {
  if (!hasDom() || squeezeCount === 0) return;

  const { count, unmount } = nextStateOnRelease(squeezeCount);
  squeezeCount = count;
  if (!unmount) return;

  document.documentElement.removeAttribute(DOCKED_ATTR);
  cancelPendingRemoval();
  removalTimer = setTimeout(removeStyleEl, SQUEEZE_DURATION_MS + STYLE_REMOVAL_GRACE_MS);
}

/**
 * Teardown escape hatch: drop the squeeze NOW regardless of ref-count, with no
 * close animation and no pending timer left behind.
 *
 * Called from the SDK's teardown (`disconnect()`), which unmounts the React root
 * and removes its container. React's effect cleanups normally release for us —
 * this is the belt for the paths where they don't run (a container dropped
 * without an unmount, an unbalanced apply): a host page left with
 * `margin-right: 400px` and no widget to remove it is unrecoverable without a
 * reload.
 */
export function forceReleaseSqueeze(): void {
  squeezeCount = 0;
  if (!hasDom()) {
    // Still drop the timer/element handles so a torn-down SDK leaks nothing.
    cancelPendingRemoval();
    styleEl = null;
    return;
  }
  cancelPendingRemoval();
  document.documentElement.removeAttribute(DOCKED_ATTR);
  removeStyleEl();
}
